import React, { useEffect, useState } from 'react';
import { Box, Typography, useTheme, useMediaQuery, Button, CircularProgress } from '@mui/material';
import { User, UcPackage } from '../types';
import { getUserByTelegramId } from '../services/api';
import { getTelegramUser } from '../services/telegram';
import { useNavigate, useLocation } from 'react-router-dom';
import { colors } from '../theme/themeConfig';
import UcPackagesList from '../components/packages/UcPackagesList';
import PageHeader from '../components/layout/PageHeader';
import { getAvailablePackages } from '../services/packages';
import { useQuery } from '@tanstack/react-query';

// Constants
const PAGE_TITLE = 'Пополнение UC';

const HomePage: React.FC = () => {
  const location = useLocation();
  const [user, setUser] = useState<User | null>(null);
  const [telegramUser, setTelegramUser] = useState<any>(null);
  const [packages, setPackages] = useState<UcPackage[]>([]);
  const navigate = useNavigate();
  const theme = useTheme();
  // Using Material UI's breakpoint system
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const username = telegramUser?.username || user?.telegramUsername || 'username';

  // Fetch packages with React Query
  const { 
    data: packagesData, 
    isLoading: isPackagesLoading, 
    error: packagesError 
  } = useQuery({
    queryKey: ['packages'],
    queryFn: getAvailablePackages,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  useEffect(() => {
    const initializeApp = async () => {
      try {
        // Get Telegram user
        const tgUser = getTelegramUser();
        setTelegramUser(tgUser);
        
        // Check for saved selections in localStorage
        const savedSelectionsString = localStorage.getItem('selectedPackages');
        let savedSelections: UcPackage[] | null = null;
        
        if (savedSelectionsString) {
          try {
            savedSelections = JSON.parse(savedSelectionsString);
          } catch (error) {
            console.error('Error parsing saved selections:', error);
            localStorage.removeItem('selectedPackages');
          }
        }
        
        // Check if we should preserve selections from the previous page
        const preserveSelections = location.state?.preserveSelections;
        const currentPackages = location.state?.currentPackages;
        
        if (preserveSelections && currentPackages) {
          setPackages(currentPackages);
        } else if (packagesData) {
          // If we have saved selections, merge them with the fetched packages
          if (savedSelections && savedSelections.length > 0) {
            // Create a map of saved selections by ID for quick lookup
            const savedSelectionsMap = savedSelections.reduce<Record<string, number>>((map, pkg: UcPackage) => {
              map[pkg.id] = pkg.selectedCodes || 0;
              return map;
            }, {});
            
            // Apply saved selections to fetched packages
            const packagesWithSavedSelections = packagesData.map(pkg => ({
              ...pkg,
              selectedCodes: savedSelectionsMap[pkg.id] || 0
            }));
            
            setPackages(packagesWithSavedSelections);
          } else {
            // Initialize selectedCodes to 0 for each package
            const packagesWithSelection = packagesData.map(pkg => ({
              ...pkg,
              selectedCodes: 0
            }));
            
            setPackages(packagesWithSelection);
          }
        }
        
        // Get user data
        if (tgUser) {
          try {
            const userData = await getUserByTelegramId(tgUser.id);
            setUser(userData);
          } catch (error) {
            console.error('Error fetching user data:', error);
          }
        } else {
          // Fallback to dummy data
          const defaultUserId = 7345174833;
          try {
            const userData = await getUserByTelegramId(defaultUserId);
            setUser(userData);
          } catch (error) {
            console.error('Error fetching user data:', error);
          }
        }
      } catch (error) {
        console.error('Error initializing app:', error);
      }
    };
    
    initializeApp();
  }, [location.state, packagesData]);

  // Update packages in localStorage when they change
  useEffect(() => {
    if (packages.length > 0) {
      const selectedPackages = packages.filter(pkg => pkg.selectedCodes && pkg.selectedCodes > 0);
      if (selectedPackages.length > 0) {
        localStorage.setItem('selectedPackages', JSON.stringify(selectedPackages));
      } else {
        localStorage.removeItem('selectedPackages');
      }
    }
  }, [packages]);

  const handleBuyNow = (pkg: UcPackage) => {
    navigate('/shop', { state: { selectedPackage: pkg } });
  };

  const handleSelectCodes = (pkg: UcPackage, count: number, shouldReset = false) => {
    setPackages(prevPackages => {
      // if (shouldReset) {
      //   return [...packages];
      // }
      return prevPackages.map(p => 
        p.id === pkg.id ? { ...p, selectedCodes: count } : p
      )
    });
  };

  const handleCheckout = () => {
    const selectedPackages = packages.filter(pkg => pkg.selectedCodes && pkg.selectedCodes > 0);
    if (selectedPackages.length > 0) {
      navigate('/shop', { state: { selectedPackages } });
    }
  };

  // Show loading state while packages are being fetched
  if (isPackagesLoading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
          backgroundColor: colors.background.default,
        }}
      >
        <CircularProgress color="primary" />
      </Box>
    );
  }

  // Show error state if packages failed to load
  if (packagesError) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
          backgroundColor: colors.background.default,
          color: colors.text.primary,
          padding: theme.spacing(2.5),
        }}
      >
        <Typography variant="h6" gutterBottom>
          Ошибка загрузки данных
        </Typography>
        <Typography variant="body2" sx={{ mb: 2 }}>
          Не удалось загрузить пакеты UC. Пожалуйста, попробуйте позже.
        </Typography>
        <Button 
          variant="contained" 
          onClick={() => window.location.reload()}
          sx={{ mt: 2 }}
        >
          Обновить страницу
        </Button>
      </Box>
    );
  }

  // Calculate selected packages for checkout button
  const selectedPackages = packages.filter(pkg => pkg.selectedCodes && pkg.selectedCodes > 0);
  const hasSelectedPackages = selectedPackages.length > 0;
  const totalPrice = selectedPackages.reduce((sum, pkg) => sum + (pkg.price * (pkg.selectedCodes || 0)), 0);
  const totalAmount = selectedPackages.reduce((sum, pkg) => sum + (pkg.amount * (pkg.selectedCodes || 0)), 0);

  return (
    <Box
      sx={{
        padding: theme.spacing(2),
        backgroundColor: colors.background.default,
        color: colors.text.primary,
        minHeight: '100vh',
        position: 'relative',
        paddingBottom: hasSelectedPackages ? theme.spacing(20) : theme.spacing(10),
      }}
    >
      <PageHeader 
        username={username}
        // title={PAGE_TITLE}
        balance={user?.bonusBalance || 0}
        photoUrl={telegramUser?.photoUrl}
      />

      <UcPackagesList 
        packages={packages}
        onBuyPackage={handleBuyNow}
        onSelectCodes={handleSelectCodes}
        loading={isPackagesLoading}
      />

      {hasSelectedPackages && (
        <Box
          sx={{
            position: 'fixed',
            bottom: 0,
            left: 0,
            right: 0,
            padding: theme.spacing(2),
            paddingBottom: '96px', 
            backgroundColor: colors.background.default,
            borderTop: `1px solid ${colors.primary.main}`,
            zIndex: 99, 
            boxShadow: '0px -4px 10px rgba(0, 0, 0, 0.1)'
          }}
        >
          <Button
            variant="contained"
            color="primary"
            fullWidth
            onClick={handleCheckout}
            sx={{
              borderRadius: theme.shape.borderRadius,
              padding: theme.spacing(1.5),
              fontSize: theme.typography.pxToRem(16),
              fontWeight: 500,
              textTransform: 'none'
            }}
          >
            Купить {totalAmount} UC за {totalPrice} рублей
          </Button>
        </Box>
      )}
    </Box>
  );
};

export default HomePage;
